<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import Auth from 'dashboard/api/auth';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Multiselect from 'vue-multiselect';
import { validateSipCredentials } from '../composables/useSipValidation';
import sipConnectionManager from '../managers/SipConnectionManager';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});
// Create authenticated axios instance
const authAxios = axios.create();
if (Auth.hasAuthCookie()) {
  const authData = Auth.getAuthData();
  Object.assign(authAxios.defaults.headers.common, {
    'access-token': authData['access-token'],
    'token-type': authData['token-type'],
    client: authData.client,
    expiry: authData.expiry,
    uid: authData.uid,
  });
}

const route = useRoute();
const store = useStore();
const agents = ref([]);
const isLoadingAgents = ref(false);
const allAgents = ref([]);
const selectedAgentsToAdd = ref([]);
const isAddingAgents = ref(false);
const agentStates = ref({});
const isReconnectingCurrentUser = ref(false);
const extensionProviderOptions = ref({});
const extensionProviderLoading = ref({});
const extensionProviderErrors = ref({});

const LIVE_STATE_POLL_INTERVAL = 15000;
const PAUSED_AVAILABILITIES = [
  'on_break',
  'at_lunch',
  'in_meeting',
  'do_not_disturb',
  'after_call_work',
];
const STATUS_BADGE_STYLES = {
  success:
    'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  info: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  warning:
    'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  danger: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  neutral: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
  accent: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400',
};

let agentStatePoller = null;

// Get account_id from route
const accountId = computed(() => route.params.accountId);
const currentUserId = computed(() => store.getters.getCurrentUserID);
const currentUser = computed(() => store.getters.getCurrentUser);

const getPluginSystem = () => {
  return window.chatwoot?.PluginSystem || window.PluginSystem || null;
};

const extensionFormProvider = computed(() => {
  const pluginSystem = getPluginSystem();
  if (!pluginSystem?.executeHook) {
    return null;
  }

  const payload = pluginSystem.executeHook('sip:extension-form-provider', {
    inbox: props.inbox,
    accountId: accountId.value,
    provider: null,
  });

  return payload?.provider || null;
});

// Get all available agents from account
const availableAgents = computed(() => {
  const currentAgentIds = agents.value.map(a => a.id);
  return allAgents.value.filter(a => !currentAgentIds.includes(a.id));
});

// Check if inbox is per_agent mode
const isPerAgentMode = computed(() => {
  const mode = props.inbox?.provider_config?.registration_mode;
  // Default to per_agent if undefined/null, otherwise strictly check
  return mode === 'per_agent' || !mode;
});

const canValidateAgainstSipServer = computed(() => {
  return Boolean(
    props.inbox?.provider_config?.sip_server &&
      props.inbox?.provider_config?.sip_transport
  );
});

const currentUserAgent = computed(() => {
  return (
    agents.value.find(
      agent => String(agent.id) === String(currentUserId.value || '')
    ) || null
  );
});

const buildCurrentUserContext = () => {
  const activeAccountId =
    store.getters.getCurrentAccountId ||
    currentUser.value?.account_id ||
    Number(accountId.value);

  if (!currentUser.value?.id || !activeAccountId) {
    return null;
  }

  return {
    ...currentUser.value,
    account_id: activeAccountId,
  };
};

// Fetch agents from store
const fetchAgents = async () => {
  if (!props.inbox?.id) return;

  isLoadingAgents.value = true;
  try {
    const response = await store.dispatch('inboxMembers/get', {
      inboxId: props.inbox.id,
    });

    if (response?.data?.payload) {
      agents.value = response.data.payload;
    }

    // eslint-disable-next-line no-use-before-define
    await maybeEnsureCurrentUserRegistration();
  } catch (error) {
    console.error('[PLUGIN][SIP-CHANNEL] Error fetching agents:', error);
    useAlert('Error loading agents');
  } finally {
    isLoadingAgents.value = false;
  }
};

// Fetch all agents from account
const fetchAllAgents = async () => {
  try {
    await store.dispatch('agents/get');
    allAgents.value = store.getters['agents/getAgents'] || [];
  } catch (error) {
    console.error('[PLUGIN][SIP-CHANNEL] Error fetching all agents:', error);
  }
};

const fetchAgentStates = async () => {
  if (!props.inbox?.id || !isPerAgentMode.value) {
    agentStates.value = {};
    return;
  }

  try {
    const response = await authAxios.get(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}/sip_agent_states`
    );

    const stateList = Array.isArray(response.data) ? response.data : [];

    agentStates.value = stateList.reduce((result, state) => {
      result[state.user_id] = state;
      return result;
    }, {});

    // eslint-disable-next-line no-use-before-define
    await maybeEnsureCurrentUserRegistration();
  } catch (error) {
    console.error('[PLUGIN][SIP-CHANNEL] Error fetching agent states:', error);
  }
};

const stopAgentStatePolling = () => {
  if (agentStatePoller) {
    clearInterval(agentStatePoller);
    agentStatePoller = null;
  }
};

const startAgentStatePolling = () => {
  stopAgentStatePolling();

  if (!props.inbox?.id || !isPerAgentMode.value) {
    return;
  }

  fetchAgentStates();
  agentStatePoller = setInterval(fetchAgentStates, LIVE_STATE_POLL_INTERVAL);
};

// Add selected agents to inbox
const addAgentsToInbox = async () => {
  if (selectedAgentsToAdd.value.length === 0) {
    useAlert('Select at least one agent');
    return;
  }

  isAddingAgents.value = true;
  try {
    const agentList = [...agents.value, ...selectedAgentsToAdd.value].map(
      a => a.id
    );

    await store.dispatch('inboxMembers/create', {
      inboxId: props.inbox.id,
      agentList,
    });

    useAlert('Agents added successfully');
    selectedAgentsToAdd.value = [];
    await fetchAgents();
    await fetchAgentStates();
  } catch (error) {
    console.error('[PLUGIN][SIP-CHANNEL] Error adding agents:', error);
    useAlert('Error adding agents');
  } finally {
    isAddingAgents.value = false;
  }
};

// Load agents on mount and when inbox changes
onMounted(() => {
  // eslint-disable-next-line no-use-before-define
  window.addEventListener('sip:state-changed', handleSipStateChanged);
  // eslint-disable-next-line no-use-before-define
  window.addEventListener('sip:error', handleSipError);
  fetchAgents();
  fetchAllAgents();
  startAgentStatePolling();
});

onBeforeUnmount(() => {
  // eslint-disable-next-line no-use-before-define
  window.removeEventListener('sip:state-changed', handleSipStateChanged);
  // eslint-disable-next-line no-use-before-define
  window.removeEventListener('sip:error', handleSipError);
  stopAgentStatePolling();
});

watch([() => props.inbox?.id, isPerAgentMode], ([inboxId, perAgentMode]) => {
  if (!inboxId) {
    stopAgentStatePolling();
    agentStates.value = {};
    return;
  }

  fetchAgents();

  if (perAgentMode) {
    startAgentStatePolling();
    // eslint-disable-next-line no-use-before-define
    maybeEnsureCurrentUserRegistration();
  } else {
    stopAgentStatePolling();
    agentStates.value = {};
  }
});

// Extension configurations per agent
const extensions = ref({});

const buildPersistedExtension = agent => ({
  extension: agent.sip_extension?.extension || '',
  password: agent.sip_extension?.password || '',
  display_name: agent.sip_extension?.display_name || agent.name || '',
});

const createExtensionDraft = agent => ({
  ...buildPersistedExtension(agent),
  provider_selection: agent.sip_extension?.extension || '',
  editing: false,
  saving: false,
  validating: false,
  validationAttempted: false,
  validationStatus: null,
  validationMessage: '',
  validationCheckedAt: null,
});

const syncExtensionDraft = agent => {
  const current = extensions.value[agent.id];
  const persisted = buildPersistedExtension(agent);

  if (!current) {
    extensions.value[agent.id] = createExtensionDraft(agent);
    return;
  }

  extensions.value[agent.id] = {
    ...current,
    extension: current.editing ? current.extension : persisted.extension,
    password: current.editing ? current.password : persisted.password,
    display_name: current.editing
      ? current.display_name
      : persisted.display_name,
    provider_selection: current.editing
      ? current.provider_selection
      : persisted.extension,
  };
};

watch(
  agents,
  newAgents => {
    const nextIds = new Set((newAgents || []).map(agent => String(agent.id)));

    Object.keys(extensions.value).forEach(agentId => {
      if (!nextIds.has(String(agentId))) {
        delete extensions.value[agentId];
        delete extensionProviderOptions.value[agentId];
        delete extensionProviderLoading.value[agentId];
        delete extensionProviderErrors.value[agentId];
      }
    });

    (newAgents || []).forEach(agent => {
      syncExtensionDraft(agent);
    });
  },
  { immediate: true, deep: true }
);

watch(
  extensionFormProvider,
  () => {
    extensionProviderOptions.value = {};
    extensionProviderLoading.value = {};
    extensionProviderErrors.value = {};
  },
  { immediate: true }
);

const resetValidationState = agentId => {
  const ext = extensions.value[agentId];
  if (!ext) return;

  ext.validationStatus = null;
  ext.validationMessage = '';
  ext.validationCheckedAt = null;
};

const markExtensionAsChanged = agentId => {
  const ext = extensions.value[agentId];
  if (!ext) return;

  ext.validationAttempted = true;
  resetValidationState(agentId);
};

const normalizedExtension = agentId => {
  return extensions.value[agentId]?.extension?.trim() || '';
};

const getExtensionValidationErrors = agentId => {
  const ext = extensions.value[agentId];
  if (!ext) {
    return [];
  }

  const errors = [];
  const extension = normalizedExtension(agentId);

  if (!extension) {
    errors.push('Extension is required.');
  } else if (/\s/.test(extension)) {
    errors.push('Extension cannot contain spaces.');
  }

  if (!ext.password) {
    errors.push('Password is required.');
  }

  if (
    extension &&
    agents.value.some(agent => {
      if (String(agent.id) === String(agentId)) {
        return false;
      }

      return normalizedExtension(agent.id) === extension;
    })
  ) {
    errors.push(
      'This extension is already assigned to another agent in this inbox.'
    );
  }

  return errors;
};

const canSaveExtension = agentId => {
  return getExtensionValidationErrors(agentId).length === 0;
};

const getExtensionProviderOptions = agentId => {
  return extensionProviderOptions.value[agentId] || [];
};

const getExtensionProviderError = agentId => {
  return extensionProviderErrors.value[agentId] || '';
};

const getExtensionProviderSelection = agent => {
  const provider = extensionFormProvider.value;
  const draft = extensions.value[agent.id];

  if (!draft) {
    return null;
  }

  if (provider?.getSelectionValue) {
    return provider.getSelectionValue({ agent, draft, inbox: props.inbox });
  }

  return draft.provider_selection || draft.extension || null;
};

const ensureExtensionProviderOptions = async agent => {
  const provider = extensionFormProvider.value;
  if (!provider?.loadAgentOptions || !props.inbox?.id) {
    return [];
  }

  if (extensionProviderOptions.value[agent.id]?.length) {
    return extensionProviderOptions.value[agent.id];
  }

  extensionProviderLoading.value[agent.id] = true;
  extensionProviderErrors.value[agent.id] = '';

  try {
    const options = await provider.loadAgentOptions({
      accountId: accountId.value,
      inbox: props.inbox,
      agent,
    });

    extensionProviderOptions.value[agent.id] = Array.isArray(options)
      ? options
      : [];

    return extensionProviderOptions.value[agent.id];
  } catch (error) {
    console.error(
      '[PLUGIN][SIP-CHANNEL] Error loading extension provider options:',
      error
    );

    extensionProviderErrors.value[agent.id] =
      error.response?.data?.message ||
      error.message ||
      'Unable to load managed extensions.';
    extensionProviderOptions.value[agent.id] = [];
    return [];
  } finally {
    extensionProviderLoading.value[agent.id] = false;
  }
};

const applyExtensionProviderSelection = async (agent, selectedValue) => {
  const provider = extensionFormProvider.value;
  const draft = extensions.value[agent.id];
  if (!provider || !draft) {
    return;
  }

  const options = await ensureExtensionProviderOptions(agent);
  const option = options.find(
    item => String(item.value) === String(selectedValue)
  );

  if (!option) {
    draft.provider_selection = selectedValue || '';
    return;
  }

  const updates = provider.applySelection?.({
    option,
    agent,
    draft,
    inbox: props.inbox,
  });

  draft.provider_selection = option.value;

  if (updates && typeof updates === 'object') {
    Object.assign(draft, updates);
  }

  markExtensionAsChanged(agent.id);
};

const toggleEdit = async agent => {
  const ext = extensions.value[agent.id];
  if (!ext) return;

  if (ext.editing) {
    extensions.value[agent.id] = createExtensionDraft(agent);
    return;
  }

  ext.editing = true;
  ext.validationAttempted = false;
  resetValidationState(agent.id);

  if (extensionFormProvider.value) {
    await ensureExtensionProviderOptions(agent);
    const selectedValue = getExtensionProviderSelection(agent);
    if (selectedValue) {
      ext.provider_selection = selectedValue;
    }
  }
};

const validateExtension = async agent => {
  const ext = extensions.value[agent.id];
  if (!ext) return false;

  ext.validationAttempted = true;
  resetValidationState(agent.id);

  const validationErrors = getExtensionValidationErrors(agent.id);
  if (validationErrors.length > 0) {
    ext.validationStatus = 'error';
    ext.validationMessage = validationErrors[0];
    return false;
  }

  if (!canValidateAgainstSipServer.value) {
    ext.validationStatus = 'error';
    ext.validationMessage =
      'Validation is unavailable until the SIP server settings are configured.';
    return false;
  }

  ext.validating = true;

  try {
    const result = await validateSipCredentials({
      sipServer: props.inbox.provider_config?.sip_server,
      sipPort: props.inbox.provider_config?.sip_port,
      sipTransport: props.inbox.provider_config?.sip_transport,
      sipRealm:
        props.inbox.provider_config?.sip_realm ||
        props.inbox.provider_config?.sip_server,
      extension: ext.extension.trim(),
      password: ext.password,
      displayName: ext.display_name || agent.name,
    });

    ext.validationStatus = result.ok ? 'success' : 'error';
    ext.validationMessage = result.message;
    ext.validationCheckedAt = new Date().toISOString();
    return result.ok;
  } catch (error) {
    ext.validationStatus = 'error';
    ext.validationMessage = error.message || 'Validation failed unexpectedly.';
    return false;
  } finally {
    ext.validating = false;
  }
};

const saveExtension = async agent => {
  const ext = extensions.value[agent.id];
  if (!ext) return;

  ext.validationAttempted = true;

  const validationErrors = getExtensionValidationErrors(agent.id);
  if (validationErrors.length > 0) {
    ext.validationStatus = 'error';
    ext.validationMessage = validationErrors[0];
    useAlert(validationErrors[0]);
    return;
  }

  ext.saving = true;

  try {
    const response = await authAxios.patch(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}/sip_extensions/${agent.id}`,
      {
        sip_extension: {
          extension: ext.extension.trim(),
          password: ext.password,
          display_name: ext.display_name || null,
        },
      }
    );

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Error saving extension');
    }

    useAlert('Extension configured successfully!');

    ext.editing = false;
    await fetchAgents();
    await fetchAgentStates();

    if (String(agent.id) === String(currentUserId.value || '')) {
      // eslint-disable-next-line no-use-before-define
      await reconnectCurrentUserExtension({ silent: true });
    }
  } catch (error) {
    console.error('[PLUGIN][SIP-CHANNEL] Error saving extension:', error);
    const message =
      error.response?.data?.errors?.join(', ') ||
      error.response?.data?.message ||
      error.message ||
      'Error saving extension';
    useAlert(message);
  } finally {
    ext.saving = false;
  }
};

const hasExtension = agentId => {
  const ext = extensions.value[agentId];
  return Boolean(ext && ext.extension && ext.password);
};

const getAgentRuntimeState = agentId => {
  const persistedState = agentStates.value[agentId] || null;

  if (
    String(agentId) !== String(currentUserId.value || '') ||
    !props.inbox?.id
  ) {
    return persistedState;
  }

  const workerState = sipConnectionManager
    .getWorker(props.inbox.id)
    ?.getState?.();
  if (!workerState) {
    return persistedState;
  }

  if (!persistedState || persistedState.sip_state !== workerState) {
    return {
      ...persistedState,
      sip_state: workerState,
      extension:
        persistedState?.extension || normalizedExtension(agentId) || null,
    };
  }

  return persistedState;
};

const formatStateTimestamp = timestamp => {
  if (!timestamp) return '';

  return new Date(timestamp).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

const formatAvailabilityLabel = availability => {
  const labels = {
    available: 'Available',
    busy: 'Busy',
    on_break: 'On break',
    at_lunch: 'At lunch',
    in_meeting: 'In meeting',
    do_not_disturb: 'Do not disturb',
    after_call_work: 'After call work',
    offline: 'Offline',
  };

  return labels[availability] || availability || 'Unknown';
};

const getAgentStatus = agentId => {
  if (!hasExtension(agentId)) {
    return {
      label: 'Pending setup',
      tone: 'warning',
      detail: 'Configure extension and password to enable SIP registration.',
    };
  }

  const state = getAgentRuntimeState(agentId);
  if (!state) {
    return {
      label: 'Configured',
      tone: 'accent',
      detail: 'No runtime status reported yet for this extension.',
    };
  }

  if (state.sip_state === 'registered') {
    if (
      PAUSED_AVAILABILITIES.includes(state.availability) ||
      state.effective_state === 'online_paused'
    ) {
      return {
        label: 'Paused',
        tone: 'warning',
        detail: `Registered, but the agent is ${formatAvailabilityLabel(
          state.availability
        ).toLowerCase()}.`,
      };
    }

    if (
      state.availability === 'busy' ||
      state.effective_state === 'online_busy'
    ) {
      return {
        label: 'Busy',
        tone: 'warning',
        detail: 'Registered, but currently marked as busy.',
      };
    }

    return {
      label: 'Registered',
      tone: 'success',
      detail: 'Registered and ready to receive SIP calls.',
    };
  }

  if (state.sip_state === 'connecting') {
    return {
      label: 'Connecting',
      tone: 'info',
      detail: 'Attempting to register this extension on the SIP server.',
    };
  }

  if (state.sip_state === 'ringing') {
    return {
      label: 'Ringing',
      tone: 'info',
      detail: 'The extension is receiving an incoming SIP call.',
    };
  }

  if (state.sip_state === 'calling') {
    return {
      label: 'Dialing',
      tone: 'info',
      detail: 'The extension is placing an outbound SIP call.',
    };
  }

  if (state.sip_state === 'in_call') {
    return {
      label: 'In call',
      tone: 'info',
      detail: 'The extension is currently in an active call.',
    };
  }

  if (state.sip_state === 'failed') {
    return {
      label: 'Inaccessible',
      tone: 'danger',
      detail:
        state.call_metadata?.error ||
        state.call_metadata?.cause ||
        'The SIP server rejected or lost this extension registration.',
    };
  }

  return {
    label: 'Disconnected',
    tone: 'neutral',
    detail: 'The extension is configured, but not currently registered.',
  };
};

const statusBadgeClass = agentId => {
  const status = getAgentStatus(agentId);
  return STATUS_BADGE_STYLES[status.tone] || STATUS_BADGE_STYLES.neutral;
};

async function reconnectCurrentUserExtension({ silent = false } = {}) {
  if (!props.inbox?.id) {
    return false;
  }

  if (!currentUserAgent.value) {
    if (!silent) {
      useAlert('Seu usuário não está adicionado como colaborador desta inbox.');
    }
    return false;
  }

  if (!hasExtension(currentUserAgent.value.id)) {
    if (!silent) {
      useAlert('Configure o seu ramal nesta inbox antes de tentar registrar.');
    }
    return false;
  }

  const userContext = buildCurrentUserContext();
  if (!userContext) {
    if (!silent) {
      useAlert(
        'Não foi possível identificar o usuário atual para registrar o ramal.'
      );
    }
    return false;
  }

  isReconnectingCurrentUser.value = true;

  try {
    await sipConnectionManager.reconnectInbox(props.inbox, userContext);

    if (!silent) {
      useAlert('Tentando registrar o seu ramal SIP novamente.');
    }

    setTimeout(() => {
      fetchAgentStates();
    }, 1500);

    return true;
  } catch (error) {
    console.error(
      '[PLUGIN][SIP-CHANNEL] Error reconnecting current user:',
      error
    );

    if (!silent) {
      useAlert(
        error.message ||
          'Falha ao iniciar um novo registro SIP para o seu ramal.'
      );
    }

    return false;
  } finally {
    isReconnectingCurrentUser.value = false;
  }
}

async function maybeEnsureCurrentUserRegistration() {
  if (
    !isPerAgentMode.value ||
    !props.inbox?.id ||
    isReconnectingCurrentUser.value
  ) {
    return;
  }

  if (!currentUserAgent.value || !hasExtension(currentUserAgent.value.id)) {
    return;
  }

  const worker = sipConnectionManager.getWorker(props.inbox.id);
  const runtimeState = getAgentRuntimeState(currentUserAgent.value.id);
  const needsRegistration =
    !worker &&
    (!runtimeState ||
      ['disconnected', 'failed'].includes(runtimeState.sip_state));

  if (!needsRegistration) {
    return;
  }

  await reconnectCurrentUserExtension({ silent: true });
}

function handleSipStateChanged(event) {
  if (String(event.detail?.inboxId) !== String(props.inbox?.id)) {
    return;
  }

  fetchAgentStates();
}

function handleSipError(event) {
  if (event.detail?.message) {
    console.warn(
      '[PLUGIN][SIP-CHANNEL] SIP runtime error:',
      event.detail.message
    );
  }

  fetchAgentStates();
}
</script>

<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <!-- Loading state -->
  <SettingsSection
    v-if="isLoadingAgents"
    title="SIP Extensions Configuration"
    sub-title="Loading agents..."
  >
    <div class="flex items-center justify-center py-8">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
      />
    </div>
  </SettingsSection>

  <!-- Not per_agent mode -->
  <SettingsSection
    v-else-if="!isPerAgentMode"
    title="Extensions Configuration"
    sub-title="This inbox is configured in shared mode"
  >
    <div class="py-4 text-sm text-slate-600 dark:text-slate-400">
      <p>
        In this mode, all agents share the same SIP extension. Calls are
        distributed automatically.
      </p>
    </div>
  </SettingsSection>

  <!-- Add agents section (always visible in per_agent mode) -->
  <SettingsSection
    v-if="!isLoadingAgents && isPerAgentMode"
    title="Add Agents"
    sub-title="Select agents to add to this inbox"
  >
    <div class="space-y-3">
      <Multiselect
        v-model="selectedAgentsToAdd"
        :options="availableAgents"
        track-by="id"
        label="name"
        multiple
        :close-on-select="false"
        :clear-on-select="false"
        hide-selected
        placeholder="Select agents to add"
        selected-label
        select-label="Press enter to select"
        deselect-label="Press enter to remove"
      />

      <NextButton
        :disabled="selectedAgentsToAdd.length === 0"
        :is-loading="isAddingAgents"
        @click="addAgentsToInbox"
      >
        Add Selected Agents
      </NextButton>
    </div>
  </SettingsSection>

  <!-- Per agent mode with agents -->
  <SettingsSection
    v-if="!isLoadingAgents && isPerAgentMode && agents.length > 0"
    title="SIP Extensions Configuration"
    sub-title="Configure extension and password for each agent to receive calls"
  >
    <div class="space-y-3">
      <div
        v-for="agent in agents"
        :key="agent.id"
        class="border border-slate-200 dark:border-slate-700 rounded-lg p-4"
      >
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-3">
            <div class="flex flex-col">
              <span
                class="text-sm font-medium text-slate-900 dark:text-slate-100"
              >
                {{ agent.name }}
              </span>
              <span class="text-xs text-slate-600 dark:text-slate-400">
                {{ agent.email }}
              </span>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <span
              v-if="!extensions[agent.id].editing"
              class="text-xs px-2 py-1 rounded"
              :class="statusBadgeClass(agent.id)"
            >
              {{ getAgentStatus(agent.id).label }}
            </span>

            <button
              type="button"
              class="text-xs px-3 py-1 rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
              @click="toggleEdit(agent)"
            >
              {{ extensions[agent.id].editing ? 'Cancel' : 'Configure' }}
            </button>
          </div>
        </div>

        <div
          v-if="extensions[agent.id].editing"
          class="space-y-3 pt-3 border-t border-slate-200 dark:border-slate-700"
        >
          <div
            v-if="extensionFormProvider"
            class="space-y-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-3 dark:border-blue-900/40 dark:bg-blue-950/20"
          >
            <label class="flex flex-col gap-1">
              <span
                class="text-xs font-medium text-slate-700 dark:text-slate-300"
              >
                {{ extensionFormProvider.label || 'Managed extension' }}
              </span>
              <select
                :value="getExtensionProviderSelection(agent) || ''"
                class="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5 text-sm"
                :disabled="
                  extensions[agent.id].saving ||
                  extensionProviderLoading[agent.id]
                "
                @focus="ensureExtensionProviderOptions(agent)"
                @change="
                  applyExtensionProviderSelection(agent, $event.target.value)
                "
              >
                <option value="">Select an extension from the provider</option>
                <option
                  v-for="option in getExtensionProviderOptions(agent.id)"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>

            <p class="mb-0 text-xs text-slate-600 dark:text-slate-400">
              Pick a managed extension to autofill the SIP credentials for this
              agent.
            </p>

            <p
              v-if="extensionProviderLoading[agent.id]"
              class="mb-0 text-xs text-slate-500 dark:text-slate-400"
            >
              Loading provider extensions...
            </p>

            <p
              v-if="getExtensionProviderError(agent.id)"
              class="mb-0 text-xs text-red-600 dark:text-red-400"
            >
              {{ getExtensionProviderError(agent.id) }}
            </p>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <label class="flex flex-col gap-1">
              <span
                class="text-xs font-medium text-slate-700 dark:text-slate-300"
              >
                Extension *
              </span>
              <input
                v-model="extensions[agent.id].extension"
                type="text"
                placeholder="e.g. 1001"
                class="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5 text-sm"
                required
                @input="markExtensionAsChanged(agent.id)"
              />
            </label>

            <label class="flex flex-col gap-1">
              <span
                class="text-xs font-medium text-slate-700 dark:text-slate-300"
              >
                Password *
              </span>
              <input
                v-model="extensions[agent.id].password"
                type="password"
                placeholder="Extension password"
                class="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5 text-sm"
                required
                @input="markExtensionAsChanged(agent.id)"
              />
            </label>
          </div>

          <label class="flex flex-col gap-1">
            <span
              class="text-xs font-medium text-slate-700 dark:text-slate-300"
            >
              Display Name (optional)
            </span>
            <input
              v-model="extensions[agent.id].display_name"
              type="text"
              :placeholder="agent.name"
              class="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5 text-sm"
              @input="markExtensionAsChanged(agent.id)"
            />
          </label>

          <div
            v-if="
              extensions[agent.id].validationAttempted &&
              getExtensionValidationErrors(agent.id).length > 0
            "
            class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200"
          >
            <div
              v-for="issue in getExtensionValidationErrors(agent.id)"
              :key="issue"
            >
              {{ issue }}
            </div>
          </div>

          <div
            v-if="extensions[agent.id].validationMessage"
            class="rounded-lg border px-3 py-2 text-xs"
            :class="
              extensions[agent.id].validationStatus === 'success'
                ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-900/40 dark:bg-green-950/20 dark:text-green-200'
                : 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-200'
            "
          >
            <div>{{ extensions[agent.id].validationMessage }}</div>
            <div
              v-if="extensions[agent.id].validationCheckedAt"
              class="mt-1 opacity-80"
            >
              Last validation:
              {{
                formatStateTimestamp(extensions[agent.id].validationCheckedAt)
              }}
            </div>
          </div>

          <div class="flex items-center justify-between gap-3">
            <span class="text-xs text-slate-500 dark:text-slate-400">
              {{
                canValidateAgainstSipServer
                  ? 'Optional: validate this extension against the SIP server before saving.'
                  : 'Validation is unavailable until the SIP server settings are configured.'
              }}
            </span>

            <div class="flex items-center gap-2">
              <NextButton
                variant="outline"
                color="slate"
                :disabled="
                  !canValidateAgainstSipServer ||
                  !canSaveExtension(agent.id) ||
                  extensions[agent.id].saving
                "
                :is-loading="extensions[agent.id].validating"
                @click="validateExtension(agent)"
              >
                Validate
              </NextButton>

              <NextButton
                :disabled="
                  !canSaveExtension(agent.id) || extensions[agent.id].validating
                "
                :is-loading="extensions[agent.id].saving"
                @click="saveExtension(agent)"
              >
                Save Extension
              </NextButton>
            </div>
          </div>
        </div>

        <div
          v-else-if="hasExtension(agent.id)"
          class="text-xs text-slate-600 dark:text-slate-400 pt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span class="font-mono">
              Extension: {{ extensions[agent.id].extension }}
            </span>
            <span>{{ getAgentStatus(agent.id).detail }}</span>
          </div>
          <div
            v-if="getAgentRuntimeState(agent.id)?.state_changed_at"
            class="mt-1 text-[11px] text-slate-500 dark:text-slate-500"
          >
            Last state update:
            {{
              formatStateTimestamp(
                getAgentRuntimeState(agent.id)?.state_changed_at
              )
            }}
          </div>
        </div>
      </div>
    </div>
  </SettingsSection>

  <!-- Empty state: no agents in per_agent mode -->
  <SettingsSection
    v-if="!isLoadingAgents && isPerAgentMode && agents.length === 0"
    title="SIP Extensions Configuration"
    sub-title="No agents added to this inbox"
  >
    <div class="py-8 text-center text-sm text-slate-600 dark:text-slate-400">
      <p class="mb-2">This inbox doesn't have any configured agents yet.</p>
      <p>
        Add agents using the "Add Agents" section above to configure their
        extensions.
      </p>
    </div>
  </SettingsSection>
</template>
<!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
